import axios from 'axios';
import { decodeTokenPayload } from '../utils/jwt';
import { IAuthResponse, ILoginCredentials } from '../interfaces/auth.interface';
import {ENDPOINTS} from "../constants/endpoints";

export const AuthService = {
    login: async (credentials: ILoginCredentials): Promise<IAuthResponse> => {
        try {
            const response = await axios.post(ENDPOINTS.AUTH.LOGIN, credentials, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'accept': '*/*'
                }
            });
            const data: IAuthResponse = response.data;
            if (data.status === 'AUTHENTICATED') {
                if (!data.token) {
                    throw new Error('No token received');
                }
                localStorage.setItem('token', data.token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            }
            return data;
        } catch (error: any) {
            console.error('Login error:', error);
            if (error.response) {
                const data = error.response.data;
                const status = error.response.status;
                const looksLikeHtml = typeof data === 'string' && /^\s*<(!doctype|html|head|body)/i.test(data);

                if (typeof data === 'string' && data.trim() && !looksLikeHtml) {
                    throw new Error(data);
                }
                if (data?.message) {
                    throw new Error(data.message);
                }
                if (status === 401 || status === 403) {
                    throw new Error('Invalid username or password.');
                }
                if (status >= 500 && status <= 599) {
                    throw new Error('The server is currently unavailable. Please try again in a few moments.');
                }
                if (status === 502 || status === 503 || status === 504) {
                    throw new Error('The server is currently unavailable. Please try again in a few moments.');
                }
                throw new Error('Login failed. Please try again.');
            }
            if (error.request) {
                throw new Error('Unable to reach the server. Please check your connection and try again.');
            }
            throw new Error('Login failed. Please try again.');
        }
    },

    verifyTwoFactor: async (challengeId: string, code: string, rememberDevice: boolean): Promise<IAuthResponse> => {
        try {
            const response = await axios.post(ENDPOINTS.AUTH.VERIFY_TWO_FACTOR, { challengeId, code, rememberDevice }, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'accept': '*/*'
                }
            });
            const data: IAuthResponse = response.data;
            if (data.status === 'AUTHENTICATED' && data.token) {
                localStorage.setItem('token', data.token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            }
            return data;
        } catch (error: any) {
            if (error.response?.data && typeof error.response.data === 'string') {
                throw new Error(error.response.data);
            }
            throw new Error('Invalid or expired code. Please try again.');
        }
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('selectedInstitutionId');
        localStorage.removeItem('selectedInstitutionCode');
        delete axios.defaults.headers.common['Authorization'];
    },

    // Set the selected institution ID in localStorage after checking approval status
    setSelectedInstitution: async (institutionId: string) => {
        try {
            // Fetch institution approval status using the dedicated endpoint
            const response = await axios.get(`${ENDPOINTS.INSTITUTION.BASE}/${institutionId}/is-approved`, {
                headers: AuthService.getAuthHeader()
            });

            const isApproved = response.data;

            // Check if institution is approved
            if (!isApproved) {
                // If not approved, logout and don't set the institution
                AuthService.logout();
                // Instead of throwing an error, return an object with error message
                return {
                    success: false,
                    error: 'Your institution is not approved yet. Please try again later.'
                };
            }

            // Check if institution is active
            const activeResponse = await axios.get(`${ENDPOINTS.INSTITUTION.BASE}/${institutionId}/is-active`, {
                headers: AuthService.getAuthHeader()
            });
            const isActive = activeResponse.data;

            if (!isActive) {
                AuthService.logout();
                return {
                    success: false,
                    error: 'Your institution has been deactivated. Please contact system administrator.'
                };
            }

            // If approved and active, store the institution ID
            localStorage.setItem('selectedInstitutionId', institutionId);
            await AuthService.cacheSelectedInstitutionCode(institutionId);
            return { success: true };
        } catch (error) {
            console.error('Error setting institution:', error);
            // For other errors, we still set the ID to avoid blocking the user completely
            localStorage.setItem('selectedInstitutionId', institutionId);
            await AuthService.cacheSelectedInstitutionCode(institutionId);
            return {
                success: false,
                error: 'Error checking institution approval status.'
            };
        }
    },

    // Get the selected institution ID from localStorage
    getSelectedInstitution: () => {
        return localStorage.getItem('selectedInstitutionId')!!;
    },

    // Get the cached institution code (used by displayUsername and other prefix-aware helpers)
    getSelectedInstitutionCode: (): string | null => {
        return localStorage.getItem('selectedInstitutionCode');
    },

    setSelectedInstitutionCodeInStorage: (code: string | null) => {
        if (code) {
            localStorage.setItem('selectedInstitutionCode', code);
        } else {
            localStorage.removeItem('selectedInstitutionCode');
        }
    },

    // Fetch and cache the institution code for a given institution ID.
    // Non-fatal: clears the cache on failure so callers fall back to showing raw usernames.
    cacheSelectedInstitutionCode: async (institutionId: string): Promise<void> => {
        try {
            const currentUser = AuthService.getCurrentUser();
            if (!currentUser?.username) {
                AuthService.setSelectedInstitutionCodeInStorage(null);
                return;
            }
            const response = await axios.get(
                `${ENDPOINTS.INSTITUTION.BASE}/codes-by-username/${encodeURIComponent(currentUser.username)}`,
                { headers: AuthService.getAuthHeader() }
            );
            const codeMap = (response.data ?? {}) as Record<string, string>;
            AuthService.setSelectedInstitutionCodeInStorage(codeMap[institutionId] ?? null);
        } catch {
            AuthService.setSelectedInstitutionCodeInStorage(null);
        }
    },

    // Check if user needs to select an institution
    needsInstitutionSelection: (institutionIds: string[] | undefined) => {
        if (!institutionIds || institutionIds.length === 0) {
            return false; // No institutions to select
        }

        if (institutionIds.length === 1) {
            // For single institution, we'll handle auto-selection separately
            // since setSelectedInstitution is now async
            return false;
        }

        // Check if we've already selected an institution
        const selectedInstitution = AuthService.getSelectedInstitution();
        if (selectedInstitution && institutionIds.includes(selectedInstitution)) {
            return false; // Already has a valid selection
        }

        return true; // Multiple institutions, needs selection
    },

    getCurrentUser: () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const payload = decodeTokenPayload(token) as Record<string, any> | null;
                if (!payload) return null;

                return {
                    token,
                    username: payload.sub,
                    roles: payload.roles,
                    name: payload.name,
                    issuedAt: payload.iat,
                    expiresAt: payload.exp,
                    issuer: payload.iss,
                    institutionIds: payload.institutionIds,
                    permissions: payload.permissions,
                    isDepartmentAdmin: payload.isDepartmentAdmin === true,
                    lang: typeof payload.lang === 'string' ? payload.lang : undefined,
                };
            } catch (error) {
                console.error('Error parsing token:', error);
                return null;
            }
        }
        return null;
    },

    getAuthHeader: () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    },

    isValidToken: () => {
        const token = localStorage.getItem('token');
        if (!token) return false;

        try {
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));

            // Check if token is expired
            const currentTime = Math.floor(Date.now() / 1000);
            if (payload.exp && payload.exp < currentTime) {
                localStorage.removeItem('token');
                delete axios.defaults.headers.common['Authorization'];
                return false;
            }

            return true;
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    },

    impersonate: async (userId: string): Promise<void> => {
        const response = await axios.post(ENDPOINTS.AUTH.IMPERSONATE(userId));
        const impersonationToken = response.data;
        const originalToken = localStorage.getItem('token')!;
        localStorage.setItem('originalToken', originalToken);
        localStorage.setItem('token', impersonationToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonationToken}`;
    },

    exitImpersonation: () => {
        const originalToken = localStorage.getItem('originalToken');
        if (originalToken) {
            localStorage.setItem('token', originalToken);
            localStorage.removeItem('originalToken');
            axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
        }
        localStorage.removeItem('selectedInstitutionId');
        localStorage.removeItem('selectedInstitutionCode');
    },

    isImpersonating: () => {
        return !!localStorage.getItem('originalToken');
    },
};
